// Copyright (C) AirWorks Solutions, Inc - All Rights Reserved
// DO NOT REDISTRIBUTE
// UNAUTHORIZED COPYING OF THIS FILE, ANY PART OR WHOLE, VIA ANY MEDIUM IS STRICTLY PROHIBITED
// PROPRIETARY AND CONFIDENTIAL

import { orderStatusCode } from 'Utils/constants';
import { sortProjects } from './sortProjects';

// AUT-6432: searching, filtering and sorting a folder's projects already in hand. The dashboard
// asks the server for these over the complete set; a folder's projects arrive whole from
// GET /folders/:id/projects, so the same narrowing happens here instead of adding query
// parameters to that endpoint - and without touching the dashboard's store.
//
// The rules mirror the server's (project_search.js: buildNameQueryMatch, buildCardOrderMatchStage):
// the keyword matches a project's name or its own reference, and a status filter admits a
// project when ANY of its orders matches - an estimate anywhere, a delivered order anywhere -
// so the folder page and the dashboard select the same projects under the same filter.
export const matchesKeyword = (project: IProject, keyword: string): boolean => {
  const needle = keyword.trim().toLowerCase();
  if (!needle) return true;
  return (project.name || '').toLowerCase().includes(needle)
    || (project.referenceId || '').toLowerCase().includes(needle);
};

// The dashboard's and the rail's Folders sections narrow to a search the same way the server
// matches folders (folder_search.js): a case-insensitive substring of the name. One rule, so
// the two sections can never disagree about which folders a search finds.
export const matchesFolderKeyword = (folder: IFolder, keyword: string): boolean => (
  (folder.name || '').toLowerCase().includes(keyword.trim().toLowerCase())
);

// Whether a project can be put on the map: it has a resolved site. One reading for the grid's
// filter, the map's pins, the rail's mark and the without-location count.
export const hasLocation = (project: { middlePoint?: unknown }): boolean => Boolean(project.middlePoint);

export const matchesOrderFilter = (project: IProject, filter: ProjectOrderSortFilter): boolean => {
  // Unassigned describes a project's relationship to folders, which says nothing
  // inside one - those options are not offered there, and are a no-op if they arrive.
  if (filter === 'all' || filter === 'unassigned') return true;
  // Location is the project's own, not an order's: the dashboard reads the same field off its
  // complete set (selectFilteredCoordinates).
  if (filter === 'noLocation') return !hasLocation(project);
  const orders = project.orders || [];
  if (filter === 'estimate') return orders.some((order) => order.isEstimate === true);
  if (filter === 'completed') return orders.some((order) => order.orderStatus === orderStatusCode.done);
  if (filter === 'canceled') return orders.some((order) => order.orderStatus === orderStatusCode.canceled);
  return true;
};

export const narrowProjects = (
  projects: IProject[],
  { keyword, orderFilter, sortFilter }: { keyword: string; orderFilter: ProjectOrderSortFilter; sortFilter: string },
): IProject[] => sortProjects(
  projects.filter((project) => matchesKeyword(project, keyword) && matchesOrderFilter(project, orderFilter)),
  sortFilter,
  keyword,
);

export default narrowProjects;
